import React from 'react';
import Image from 'next/image';
import { ISponsor } from '../../models/sponsor';

// T001: TierId type definition
type TierId = 'naamgevend' | 'maatschappelijk' | 'media' | 'official' | 'suppliers';

// T002: ITierConfig interface
interface ITierConfig {
	id: TierId;
	displayOrder: number;
	defaultHeading: string;
	matchPatterns: string[] | null;
}

// T003: ITierGroup interface
interface ITierGroup {
	tier: ITierConfig;
	sponsors: ISponsor[];
}

// T004: TIER_CONFIG constant array with 5 tier definitions
const TIER_CONFIG: ITierConfig[] = [
	{ id: 'naamgevend', displayOrder: 1, defaultHeading: 'Naamgevend Partner', matchPatterns: ['naamgevend partner'] },
	{
		id: 'maatschappelijk',
		displayOrder: 2,
		defaultHeading: 'Premium Partner',
		matchPatterns: ['maatschappelijk partner'],
	},
	{
		id: 'official',
		displayOrder: 3,
		defaultHeading: 'Official partner',
		matchPatterns: ['official partner', 'automotive'],
	},
	{ id: 'media', displayOrder: 4, defaultHeading: 'Media partner', matchPatterns: ['media partner'] },
	{ id: 'suppliers', displayOrder: 5, defaultHeading: 'Suppliers', matchPatterns: null },
];

// T005: Classify a sponsor into a tier based on subtitle (case-insensitive substring matching)
function classifySponsor(sponsor: ISponsor): TierId {
	const subtitle = (sponsor.sponsor_fields?.subtitle || '').toLowerCase().trim();

	for (const tier of TIER_CONFIG) {
		if (tier.matchPatterns === null) continue; // Skip fallback tier in loop
		for (const pattern of tier.matchPatterns) {
			if (subtitle.includes(pattern.toLowerCase())) {
				return tier.id;
			}
		}
	}

	return 'suppliers'; // Fallback tier
}

// T006: Group sponsors by tier, preserving original order within each group
function groupSponsorsByTier(sponsors: ISponsor[]): ITierGroup[] {
	const groups: Map<TierId, ISponsor[]> = new Map();

	// Initialize empty arrays for each tier
	for (const tier of TIER_CONFIG) {
		groups.set(tier.id, []);
	}

	// Classify each sponsor
	for (const sponsor of sponsors) {
		const tierId = classifySponsor(sponsor);
		groups.get(tierId)!.push(sponsor);
	}

	// Convert to array, filter empty tiers (T013: Empty tier handling), maintain display order
	return TIER_CONFIG.map((tier) => ({ tier, sponsors: groups.get(tier.id)! })).filter(
		(group) => group.sponsors.length > 0,
	);
}

// T010: Add headingOverrides prop to ISponsorGridProps interface
interface ISponsorGridProps {
	sponsors: ISponsor[];
	className?: string;
	headingOverrides?: Partial<Record<TierId, string>>;
}

// T007-T008, T011: Update SponsorGrid to use groupSponsorsByTier and accept headingOverrides
export const SponsorGrid: React.FC<ISponsorGridProps> = ({ sponsors, className = '', headingOverrides = {} }) => {
	// T014: Verify existing empty state message still displays when sponsors array is empty/null
	if (!sponsors || sponsors.length === 0) {
		return (
			<div className="text-center py-16">
				<p className="text-gray-600 text-lg">
					Partnerships zijn in ontwikkeling. Neem contact op voor meer informatie.
				</p>
			</div>
		);
	}

	const tierGroups = groupSponsorsByTier(sponsors);

	return (
		<div className={className}>
			{tierGroups.map((group) => (
				<div key={group.tier.id} className="mb-12">
					{/* T012: Use headingOverrides[tierId] ?? tier.defaultHeading */}
					<h2 className="text-2xl font-bold mb-6 text-gray-900">
						{headingOverrides[group.tier.id] ?? group.tier.defaultHeading}
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
						{group.sponsors.map((sponsor, index) => (
							<SponsorCard key={index} sponsor={sponsor} />
						))}
					</div>
				</div>
			))}
		</div>
	);
};

interface ISponsorCardProps {
	sponsor: ISponsor;
}

const SponsorCard: React.FC<ISponsorCardProps> = ({ sponsor }) => {
	const imageUrl = sponsor.featuredImage?.node?.sourceUrl || sponsor.sponsor_fields.invertedImage?.sourceUrl;
	const websiteUrl = sponsor.sponsor_fields.websiteUrl;

	const cardContent = (
		<div
			className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300
				flex items-center justify-center p-6 h-40"
		>
			<div className="flex flex-col h-full items-center justify-center gap-y-1 w-full">
				{imageUrl && (
					<div className="relative w-3/4 h-full">
						<Image
							src={imageUrl}
							alt={sponsor.featuredImage?.node?.altText || 'Sponsor logo'}
							fill
							className="object-contain"
						/>
					</div>
				)}
				<div className="text-gray-400 text-center">
					<p className="font-semibold text-xs uppercase ">{sponsor.sponsor_fields.subtitle || 'Sponsor'}</p>
				</div>
			</div>
		</div>
	);

	if (websiteUrl) {
		return (
			<a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="block">
				{cardContent}
			</a>
		);
	}

	return cardContent;
};

export default SponsorGrid;
